/* eslint-disable complexity */
import React, { useEffect, useRef } from 'react';
import {
	checkValidation,
	getHelperText,
	ICountry,
	ICustomer,
	ITypePersonalDataFields,
	IValidatedState,
	ValidatedState,
	Variants,
} from '@adac-mp/shared';
import {
	POIIcon,
	Title,
	Dropdown,
	Input,
	RadioButtons,
	Typography,
	MemberInput,
	EmailInput,
	HintMessage,
	getHintContent,
} from '../../index';
import { useValidatedStates } from '../../hooks/useValidatedStates';

interface ICustomerDataProps {
	readonly?: boolean;
	variant: Variants;
	labels?: ITypePersonalDataFields;
	hasMembershipBenefit?: boolean;
	customer?: ICustomer;
	validateFields?: boolean;
	allCountries?: ICountry[];
	setCustomerFilled?: (buttonState: boolean) => void;
	updateCustomer?: React.Dispatch<React.SetStateAction<ICustomer | undefined>>;
}

const getFields = (variant: Variants) => {
	switch (variant) {
		case 'dkv':
			return [
				'salutationId',
				'title',
				'firstName',
				'lastName',
				'street',
				'nr',
				'zip',
				'city',
				'country',
				'countryCodeIso2',
				'email',
				'phone',
			];
		case 'vignette':
			return ['firstName', 'lastName', 'street', 'zip', 'city', 'country', 'countryCodeIso2', 'email'];
		case 'updatepayment':
			return ['firstName', 'lastName', 'street', 'zip', 'city', 'country', 'countryCodeIso2'];
		default:
			return [];
	}
};

export const CustomerData = ({
	readonly,
	variant,
	labels = undefined,
	customer,
	hasMembershipBenefit = false,
	validateFields,
	allCountries,
	setCustomerFilled = () => {},
	updateCustomer = () => {},
}: ICustomerDataProps) => {
	const fields = getFields(variant);

	const fieldMap = {
		salutationId: customer?.person?.salutationId,
		title: customer?.person?.titleId,
		firstName: customer?.person?.firstName,
		lastName: customer?.person?.lastName,
		street: customer?.addresses ? customer?.addresses[0].street : undefined,
		nr: customer?.addresses ? customer?.addresses[0].houseNo : undefined,
		zip: customer?.addresses ? customer?.addresses[0].zip : undefined,
		city: customer?.addresses ? customer?.addresses[0].city : undefined,
		country: customer?.addresses ? customer.addresses[0].country : 'Deutschland',
		countryCodeIso2: customer?.addresses ? customer.addresses[0].countryCodeIso2 : 'DE',
		email: customer?.communications[0] ? customer?.communications[0].details : undefined,
		phone: customer?.communications[1] ? customer?.communications[1].details : undefined,
	};

	const stateInit = {};
	fields.forEach((field) => {
		stateInit[field] = {
			value: fieldMap[field],
			error: false,
			warning: false,
			required: !(field === 'title'),
			confirmed: field !== 'email',
		};
	});

	const inputWrapperRef = useRef<HTMLDivElement | null>(null);
	const [state, { update, updateWithValidation, updateWithoutError, updateWithConfirmationAndError, hasErrors }] =
		useValidatedStates(stateInit);
	const isGermany = state.countryCodeIso2.value === 'DE';

	useEffect(() => {
		const states: IValidatedState[] = Object.values(state);
		const allFilled = states.every(({ value, required, confirmed }) =>
			required ? value && !hasErrors() && confirmed : true,
		);
		setCustomerFilled(allFilled);

		const getEmailStateValue = () => {
			if (state.email) {
				return state.email.error ? undefined : state.email.value;
			}
			return undefined;
		};

		updateCustomer({
			person: {
				firstName: state.firstName.value,
				lastName: state.lastName.value,
				titleId: state.title ? Number(state.title.value) : 0,
				salutationId: state.salutationId ? Number(state.salutationId.value) : undefined,
				memberNo: customer?.person?.memberNo,
			},
			addresses: [
				{
					street: state.street.value,
					houseNo: state.nr?.value,
					zip: state.zip.value,
					city: state.city.value,
					country: state.country.value,
					countryCodeIso2: state.countryCodeIso2.value,
				},
			],
			communications: [
				{
					typeId: 1,
					details: getEmailStateValue(),
				},
				{
					typeId: 2,
					details: state.phone ? state.phone.value : undefined,
				},
			],
		} as ICustomer);
	}, [state]);

	useEffect(() => {
		if (validateFields) {
			const currStates: [string, ValidatedState][] = Object.entries(state);
			currStates.forEach(([id, currState]) =>
				updateWithConfirmationAndError(
					id,
					currState.value ?? '',
					currState.required ? !currState.value || currState.error : false,
					Boolean(currState.confirmed),
				),
			);
		}
	}, [validateFields]);

	const validateZipCode = (value: string) =>
		isGermany
			? !checkValidation(value, 'ZIP_GERMANY') && value.length <= 5
			: !checkValidation(value, 'ZIP') && value.length <= 20 && value.length >= 4;

	useEffect(() => {
		if (state.zip.value) {
			updateWithValidation('zip', state.zip.value, () => !validateZipCode(state.zip.value));
		}
	}, [isGermany]);

	const renderCustomerDataHeadline = () => {
		let title: string;
		switch (variant) {
			case 'dkv':
				title = 'Liefer- & Rechnungsadresse';
				break;
			default:
				title = labels?.personalDataTitle || 'Persönliche Daten';
		}
		return title;
	};

	const updateEmailValue = (value: string, validationStates?: { error?: boolean; confirmed?: boolean }) => {
		updateWithConfirmationAndError(
			'email',
			value,
			validationStates?.error ?? false,
			validationStates?.confirmed ?? false,
		);
	};

	return (
		<div ref={inputWrapperRef}>
			<Typography color="title" variant="header" size="xxl" component="h4" className="px-4 mb-4 mt-5">
				{renderCustomerDataHeadline()}
			</Typography>
			{variant === 'updatepayment' && (
				<HintMessage
					className="mt-4 mb-5"
					hints={getHintContent(
						'flexUpdateHint',
						[
							{
								tip: 'Hinweis:',
								message:
									'Bitte beachten Sie, dass das neue Zahlungsmittel auf Sie als Besteller lauten muss.',
							},
						],
						labels?.hint,
					)}
				/>
			)}
			{state.salutationId && (
				<RadioButtons
					hasError={state.salutationId.error}
					helperText={getHelperText('SALUTATION')}
					helperTextStyle={{ marginTop: '10px', gap: '12px', paddingInline: '16px', paddingBottom: '8px' }}
					wrapperClassName="flex bg-white rounded"
					className="w-1/3 pl-4 border-r border-solid border-y-0 border-l-0 border-r-gray-600 py-[14px] md:py-3 last:border-r-0 cursor-pointer"
					labelClassName="w-full"
					value={String(state.salutationId.value)}
					setValue={(val) => {
						updateWithValidation('salutationId', val, () => checkValidation(val, 'STRING'));
					}}
					options={[
						{ id: '1', label: 'Herr', title: 'Herr' },
						{
							id: '2',
							label: 'Frau',
							title: 'Frau',
						},
						{
							id: '3',
							label: 'Divers',
							title: 'Divers',
						},
					]}
				/>
			)}
			{state.title && (
				<Dropdown
					data-cy="Dropdown: titel"
					items={[
						{ label: 'Kein Titel', value: '0' },
						{ label: 'Dr.', value: '1' },
						{ label: 'Prof.', value: '2' },
					]}
					placeholder="Titel"
					icon={<Title />}
					iconStyle={{ left: 24 }}
					style={{ paddingLeft: 60 }}
					value={state.title.value}
					setValue={(value) => {
						updateWithoutError('title', value);
					}}
					className="w-full mt-5"
				/>
			)}
			<Input
				placeholder={labels?.name || 'Vorname'}
				value={readonly ? customer?.person?.firstName : state.firstName.value}
				readonly={readonly}
				mandatory={state.firstName.required}
				hasError={state.firstName.error && state.firstName.required}
				helperText={labels?.nameError || getHelperText('FIRSTNAME')}
				name="fist-name"
				autocomplete="given-name"
				onBlur={(e) => {
					updateWithValidation('firstName', e.target.value, () => checkValidation(e.target.value, 'STRING'));
				}}
				onChange={(e) => {
					updateWithoutError('firstName', e.target.value);
				}}
			/>
			<Input
				placeholder={labels?.lastName || 'Nachname'}
				value={readonly ? customer?.person?.lastName : state.lastName.value}
				readonly={readonly}
				mandatory={state.lastName.required}
				hasError={state.lastName.error && state.lastName.required}
				helperText={labels?.lastNameError || getHelperText('LASTNAME')}
				name="last-name"
				autocomplete="family-name"
				onBlur={(e) => {
					updateWithValidation('lastName', e.target.value, () => checkValidation(e.target.value, 'STRING'));
				}}
				onChange={(e) => {
					updateWithoutError('lastName', e.target.value);
				}}
			/>
			<div className="flex flex-wrap w-full mt-5 md:flex-nowrap">
				<Input
					icon={<POIIcon />}
					iconWrapperClassName="-left-0.5"
					value={readonly && customer?.addresses ? customer.addresses[0].street : state.street.value}
					readonly={readonly}
					placeholder={
						state.nr ? labels?.street || 'Straße' : labels?.streetAndHousenumber || 'Straße und Hausnummer'
					}
					widthClasses={`w-full ${state.nr ? 'md:w-4/6 md:mr-1.5' : ''}`}
					mandatory={state.street.required}
					hasError={state.street.error}
					helperText={
						state.nr
							? labels?.streetError || getHelperText('STREET')
							: labels?.streetAndHousenumberError || getHelperText('STREETANDHOUSENUMBER')
					}
					name="street"
					autocomplete="address-line1"
					onBlur={(e) => {
						updateWithValidation('street', e.target.value, () => checkValidation(e.target.value, 'STRING'));
					}}
					onChange={(e) => {
						updateWithoutError('street', e.target.value);
					}}
				/>
				{state.nr && (
					<Input
						placeholder={labels?.streetNumber || 'Nr'}
						widthClasses="w-full md:w-2/6"
						value={readonly && customer?.addresses ? customer.addresses[0].houseNo : state.nr.value}
						mandatory={state.nr.required}
						hasError={state.nr.error}
						helperText={labels?.streetNumberError || getHelperText('HOUSENUMBER')}
						name="houseNumber"
						autocomplete="address-line2"
						onBlur={(e) => {
							updateWithValidation('nr', e.target.value, () => checkValidation(e.target.value, 'STRING'));
						}}
						onChange={(e) => {
							updateWithoutError('nr', e.target.value);
						}}
					/>
				)}
			</div>
			<div className="flex flex-wrap md:flex-nowrap">
				<Input
					placeholder={labels?.zipCode || 'PLZ'}
					widthClasses="w-full md:w-2/6 md:mr-1.5"
					value={readonly && customer?.addresses ? customer.addresses[0].zip : state.zip.value}
					readonly={readonly}
					mandatory={state.zip.required}
					hasError={state.zip.error}
					helperText={labels?.zipCodeError || getHelperText('ZIP_GERMANY')}
					name="zip"
					autocomplete="postal-code"
					onBlur={(e) => {
						updateWithValidation('zip', e.target.value, () => !validateZipCode(e.target.value));
					}}
					onChange={(e) => {
						updateWithoutError('zip', e.target.value);
					}}
				/>
				<Input
					placeholder={labels?.city || 'Ort'}
					widthClasses="w-full md:w-4/6"
					value={readonly && customer?.addresses ? customer.addresses[0].city : state.city.value}
					readonly={readonly}
					mandatory={state.city.required}
					hasError={state.city.error}
					helperText={labels?.cityError || getHelperText('LOCATION')}
					name="city"
					autocomplete="address-level2"
					onBlur={(e) => {
						updateWithValidation('city', e.target.value, () => checkValidation(e.target.value, 'STRING'));
					}}
					onChange={(e) => {
						updateWithoutError('city', e.target.value);
					}}
				/>
			</div>
			{allCountries && !readonly ? (
				<Dropdown
					className="w-full"
					placeholder={labels?.country || 'Land'}
					value={state.country.value}
					items={allCountries.map(({ name, isoCode }) => ({ label: name, value: name, id: isoCode }))}
					setValue={(currValue, currId) => {
						updateWithValidation('country', currValue, () => checkValidation(currValue, 'STRING'));
						if (currId) {
							update('countryCodeIso2', currId);
						}
					}}
				/>
			) : (
				<Input
					readonly
					placeholder={labels?.country || 'Land'}
					value={readonly && customer?.addresses ? customer.addresses[0].country : state.country.value}
					mandatory={state.country.required}
					name="country"
					autocomplete="address-level2"
				/>
			)}
			<div className="mt-5">
				{state.email && (
					<EmailInput
						readonly={readonly}
						labels={{ email: labels?.email, emailError: labels?.emailError }}
						customer={customer}
						state={state.email}
						updateEmail={updateEmailValue}
					/>
				)}
				{state.phone && (
					<Input
						placeholder="Telefonnummer"
						type="tel"
						value={state.phone.value}
						mandatory={state.phone.required}
						hasError={state.phone.error}
						helperText={getHelperText('PHONE')}
						name="phone"
						autocomplete="tel"
						onBlur={(e) => {
							updateWithValidation('phone', e.target.value, () =>
								checkValidation(e.target.value, 'PHONE'),
							);
						}}
						onChange={(e) => {
							if (e.target.value === '' || !checkValidation(e.target.value, 'INTEGERS')) {
								updateWithoutError('phone', e.target.value);
							}
						}}
					/>
				)}
				{/* Business Logic: Rendering MemberInput without benefits and as optional Input */}
				{!hasMembershipBenefit && !readonly && (
					<MemberInput
						customer={customer}
						setMemberSuccess={setCustomerFilled}
						setCustomer={updateCustomer}
					/>
				)}
			</div>
		</div>
	);
};
